import { $generateJSONFromSelectedNodes, $generateNodesFromSerializedNodes } from "@lexical/clipboard";
import {
  $createCodeNode,
  $isCodeHighlightNode,
  $isCodeNode,
  CodeHighlightNode,
  CodeNode as CodeBlockNode,
} from "@lexical/code";
import { createHeadlessEditor } from "@lexical/headless";
import { $isLinkNode, AutoLinkNode, LinkNode } from "@lexical/link";
import {
  $insertList,
  $isListItemNode,
  ListItemNode,
  ListNode,
  type ListType,
  REMOVE_LIST_COMMAND,
} from "@lexical/list";
import { MarkNode } from "@lexical/mark";
import { $convertFromMarkdownString, $convertToMarkdownString } from "@lexical/markdown";
import { $createHeadingNode, $createQuoteNode, $isQuoteNode, HeadingNode, QuoteNode } from "@lexical/rich-text";
import { $isAtNodeEnd, $setBlocksType } from "@lexical/selection";
import {
  $createTableNodeWithDimensions,
  $isTableCellNode,
  $isTableNode,
  $isTableRowNode,
  TableCellNode,
  TableNode,
  TableRowNode,
} from "@lexical/table";
import { $dfs, $getNearestNodeOfType, $unwrapNode } from "@lexical/utils";
import {
  $createLineBreakNode,
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $getSelection,
  $isDecoratorNode,
  $isElementNode,
  $isLineBreakNode,
  $isParagraphNode,
  $isRangeSelection,
  $isRootOrShadowRoot,
  $isTextNode,
  type EditorState,
  ElementNode,
  type LexicalEditor,
  type LexicalNode,
  type RangeSelection,
  type SerializedEditorState,
  type SerializedLexicalNode,
  type SerializedTextNode,
  TabNode,
} from "lexical";
import { merge } from "ts-deepmerge";
import { ref } from "vue";

import { streamPostRequest } from "~/api/backendOld";
import { getProperty, getPropertyPartialTask } from "~/common/properties";
import { SORTED_LANGUAGES } from "~/constants/language";
import { RecommendationIcon, RecommendTranslateIcon } from "~/icons";
import { EditorMode, Language, RecommendationKind } from "~/shared/enums";
import type { Task, TextBlockType, TypeaheadOption } from "~/shared/types";
import { usePageStore } from "~/stores";

import { RECOMMENDATION_KIND_TO_ICON_MAP, RECOMMENDATION_SECTION_KEY_AI } from "./const";
import { AiCursorNode } from "./nodes/AiCursorNode";
import { AttachmentNode } from "./nodes/AttachmentNode";
import { DartLinkNode } from "./nodes/DartLinkNode";
import type { SerializedEntityNode } from "./nodes/EntityNode";
import { EntityNode } from "./nodes/EntityNode";
import { $isHorizontalRuleNode, HorizontalRuleNode } from "./nodes/HorizontalRuleNode";
import { RecommendationButtonsNode } from "./nodes/RecommendationButtonsNode";
import { RecommendationLoadingNode } from "./nodes/RecommendationLoadingNode";
import { RecommendationWrapperNode } from "./nodes/RecommendationWrapperNode";
import { SkillChipNode } from "./nodes/SkillChipNode";
import type { SerializedSmartMatchNode } from "./nodes/SmartMatchNode";
import { ToggleButtonNode } from "./nodes/ToggleButtonNode";
import { ToggleContentNode } from "./nodes/ToggleContentNode";
import { $isToggleDetailsNode, ToggleDetailsNode } from "./nodes/ToggleDetailsNode";
import { $isToggleTitleNode, ToggleTitleNode } from "./nodes/ToggleTitleNode";
import { ToggleWrapperNode } from "./nodes/ToggleWrapperNode";
import { $isTypeaheadNode, type SerializedTypeaheadNode, TypeaheadNode } from "./nodes/TypeaheadNode";
import { TRANSFORMERS } from "./transformers";

type CreateElementNodeFunc = () => ElementNode;

type UpdateListenerArg = {
  dirtyElements: Map<string, boolean>;
  dirtyLeaves: Set<string>;
  editorState: EditorState;
  normalizedNodes: Set<string>;
  prevEditorState: EditorState;
  tags: Set<string>;
};

type InputResult = {
  normal: boolean;
  atEnd: boolean | undefined;
  anchorNode: LexicalNode | undefined;
  anchorOffset: number | undefined;
};

const BLOCK_TYPE_TO_CREATE_FUNCTION_MAP = new Map<TextBlockType, CreateElementNodeFunc>([
  ["paragraph", $createParagraphNode],
  ["h1", () => $createHeadingNode("h1")],
  ["h2", () => $createHeadingNode("h2")],
  ["h3", () => $createHeadingNode("h3")],
  ["quote", $createQuoteNode],
  ["code", $createCodeNode],
]);

const RECOMMENDATION_ICON_CLASS = "icon-sm text-recommendation-base";

const EMPTY_CONTENT_DISABLED_REASON = "Please add some content first";
const EMPTY_SELECTION_DISABLED_REASON = "Please select some content first";
const EMPTY_TITLE_DISABLED_REASON = "Please enter a title first";

const getEmptyContentDisabledReason = (isEditorEmpty?: boolean, isSelectionEmpty?: boolean) =>
  isSelectionEmpty && !isEditorEmpty ? EMPTY_SELECTION_DISABLED_REASON : EMPTY_CONTENT_DISABLED_REASON;

export const GENERIC_EDITOR_NODES = [
  AutoLinkNode,
  CodeBlockNode,
  CodeHighlightNode,
  HeadingNode,
  LinkNode,
  ListItemNode,
  ListNode,
  MarkNode,
  QuoteNode,
  TabNode,
  TableCellNode,
  TableNode,
  TableRowNode,
  // custom nodes
  HorizontalRuleNode,
  ToggleButtonNode,
  ToggleContentNode,
  ToggleDetailsNode,
  ToggleTitleNode,
  ToggleWrapperNode,
  TypeaheadNode,
];

export const PARTIAL_DART_EDITOR_NODES = [
  ...GENERIC_EDITOR_NODES,
  AttachmentNode,
  DartLinkNode,
  EntityNode,
  SkillChipNode,
];

export const FULL_DART_EDITOR_NODES = [
  ...PARTIAL_DART_EDITOR_NODES,
  AiCursorNode,
  RecommendationButtonsNode,
  RecommendationLoadingNode,
  RecommendationWrapperNode,
];

export const getHeadlessEditorWithNodes = () => createHeadlessEditor({ nodes: FULL_DART_EDITOR_NODES });

export const loadHeadlessEditorState = (editor: LexicalEditor, state: SerializedEditorState) => {
  editor.setEditorState(editor.parseEditorState(state));
  return editor;
};

// Copied from: https://github.com/wobsoriano/lexical-vue/blob/main/playground/src/utils.ts
export const getSelectedNode = (selection: RangeSelection) => {
  const { anchor, focus } = selection;
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();

  if (anchorNode === focusNode) {
    return anchorNode;
  }

  const isBackward = selection.isBackward();
  if (isBackward) {
    return $isAtNodeEnd(focus) ? anchorNode : focusNode;
  }

  return $isAtNodeEnd(anchor) ? focusNode : anchorNode;
};

const isTableContentNode = (node: LexicalNode) => $isTableNode(node) || $isTableRowNode(node) || $isTableCellNode(node);

export const isEmpty = (node: LexicalNode) => {
  if (isTableContentNode(node)) {
    return false;
  }

  if (!$isElementNode(node)) {
    return node.getTextContentSize() === 0;
  }
  const numChildren = node.getChildrenSize();
  if (numChildren !== 1) {
    return numChildren === 0;
  }

  const child = node.getFirstChild();
  if (!$isTextNode(child) && !$isTypeaheadNode(child) && !$isHorizontalRuleNode(child)) {
    return false;
  }
  return child.getTextContentSize() === 0;
};

const hasTableAncestor = (node: LexicalNode) => {
  for (let curr: LexicalNode | null = node; curr !== null; curr = curr.getParent()) {
    if (isTableContentNode(curr)) {
      return true;
    }
  }
  return false;
};

export const $isBlock = (node: LexicalNode): node is ElementNode => {
  if ($isDecoratorNode(node)) {
    return false;
  }
  if (!$isElementNode(node) || $isRootOrShadowRoot(node)) {
    return false;
  }
  const firstChild = node.getFirstChild();
  const isLeafElement =
    firstChild === null ||
    $isLineBreakNode(firstChild) ||
    $isTextNode(firstChild) ||
    $isTypeaheadNode(firstChild) ||
    firstChild.isInline();
  return !node.isInline() && node.canBeEmpty() !== false && isLeafElement;
};

const $flattenListItems = (selection: RangeSelection) => {
  const items = new Set(
    selection
      .getNodes()
      .map((node) => $getNearestNodeOfType(node, ListItemNode))
      .filter($isListItemNode)
  );
  items.forEach((item) =>
    $dfs(item).forEach(({ node }) => {
      if ($isQuoteNode(node) || $isParagraphNode(node)) {
        if (node.getPreviousSibling()) {
          node.insertBefore($createLineBreakNode());
        }
        $unwrapNode(node);
      }
    })
  );
};

export const createTextBlockNode = (blockType: TextBlockType) => {
  const createNodeFunc = BLOCK_TYPE_TO_CREATE_FUNCTION_MAP.get(blockType);
  if (!createNodeFunc) {
    return;
  }
  const selection = $getSelection() as RangeSelection;
  if (!$isRangeSelection(selection)) {
    return;
  }
  if (blockType === "quote") {
    $flattenListItems(selection);
  }
  const blocks = new Map<string, ElementNode>();
  $setBlocksType(selection, () => {
    const node = createNodeFunc();
    blocks.set(node.getKey(), node);
    return node;
  });

  if (blockType === "code") {
    blocks.forEach((node) => {
      const previous = node.getPreviousSibling();
      if ($isCodeNode(previous) && blocks.has(previous.getKey())) {
        previous.append($createLineBreakNode(), ...node.getChildren());
        node.remove();
      }
    });
  }
};

const formatList = (type: ListType | null, listType: ListType, editor: LexicalEditor) => {
  if (type !== listType) {
    editor.update(() => {
      $insertList(listType);
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $flattenListItems(selection);
      }
    });
    return;
  }
  editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
};

export const formatBulletList = (type: ListType | null, editor: LexicalEditor) => formatList(type, "bullet", editor);

export const formatNumberedList = (type: ListType | null, editor: LexicalEditor) => formatList(type, "number", editor);

export const formatCheckList = (type: ListType | null, editor: LexicalEditor) => formatList(type, "check", editor);

export const insertTable = (editor: LexicalEditor) => {
  editor.update(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) {
      return;
    }

    const selectedLines = selection.isCollapsed() ? [] : selection.getTextContent().split(/\r?\n/);
    if (!selection.isCollapsed()) {
      selection.removeText();
    }

    const currentSelection = $getSelection();
    if (!$isRangeSelection(currentSelection)) {
      return;
    }

    const anchor = currentSelection.anchor.getNode();
    const topLevelElement = anchor.getTopLevelElementOrThrow();
    const rowCount = Math.max(3, selectedLines.length || 1);
    const tableNode = $createTableNodeWithDimensions(rowCount, 3, { rows: true, columns: false });

    tableNode.getChildren().forEach((rowNode, rowIndex) => {
      if (!$isTableRowNode(rowNode)) {
        return;
      }

      rowNode.getChildren().forEach((cellNode, columnIndex) => {
        if (!$isTableCellNode(cellNode)) {
          return;
        }
        if (columnIndex !== 0 || selectedLines.length === 0 || selectedLines[rowIndex] === undefined) {
          return;
        }
        const paragraphNode = cellNode.getFirstChild();
        if (!$isElementNode(paragraphNode)) {
          return;
        }
        paragraphNode.clear();
        paragraphNode.append($createTextNode(selectedLines[rowIndex]));
      });
    });

    topLevelElement.insertAfter(tableNode);
    tableNode.selectStart();
  });
};

export const isLineLevelNode = (node: LexicalNode) =>
  !($isTextNode(node) || $isTypeaheadNode(node) || $isCodeHighlightNode(node) || $isLinkNode(node));

export const getUpdateDetails = (arg: UpdateListenerArg): InputResult => {
  const { dirtyLeaves, editorState, prevEditorState, tags } = arg;
  const response: InputResult = { normal: false, atEnd: undefined, anchorNode: undefined, anchorOffset: undefined };

  if (tags.has("historic")) {
    return response;
  }

  const selection = editorState.read($getSelection);
  const prevSelection = prevEditorState.read($getSelection);
  if (!$isRangeSelection(prevSelection) || !$isRangeSelection(selection) || !selection.isCollapsed()) {
    return response;
  }

  const anchorKey = selection.anchor.key;
  const anchorNode = editorState._nodeMap.get(anchorKey);
  if (!($isTextNode(anchorNode) || $isTypeaheadNode(anchorNode)) || !dirtyLeaves.has(anchorKey)) {
    return response;
  }

  const anchorOffset = selection.anchor.offset;
  const atEnd = anchorOffset === 1 || anchorOffset === prevSelection.anchor.offset + 1;
  return { normal: true, atEnd, anchorNode, anchorOffset };
};

const trimTextNodeContent = (curr: LexicalNode, direction: "start" | "end") => {
  const original = curr.getTextContent();
  const trim = direction === "start" ? original.trimStart() : original.trimEnd();
  if (trim.length === 0) {
    curr.remove(true);
    return false;
  }
  if (trim.length < original.length && $isTextNode(curr)) {
    const [start, end] = direction === "start" ? [0, original.length - trim.length] : [trim.length, original.length];
    curr.select(start, end);
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      selection.removeText();
    }
  }
  return true;
};

export const trimLexicalState = () => {
  const root = $getRoot();

  // Trim the starting children and starting text
  let stack: LexicalNode[] = [root];
  for (let i = 0; i < 10000; i += 1) {
    const curr = stack[stack.length - 1];

    if (curr === undefined || $isDecoratorNode(curr)) {
      break;
    }

    if ($isTextNode(curr) || $isTypeaheadNode(curr)) {
      if (trimTextNodeContent(curr, "start")) {
        break;
      }
      stack.pop();
      continue;
    }

    const child = $isElementNode(curr) ? curr.getFirstChild() : null;
    if (child === null) {
      if (curr !== root && hasTableAncestor(curr)) {
        break;
      }
      if (curr !== root) {
        curr.remove(true);
      }
      stack.pop();
      continue;
    }

    stack.push(child);
  }

  // Trim the ending children and ending text
  stack = [root];
  for (let i = 0; i < 10000; i += 1) {
    const curr = stack[stack.length - 1];

    if (curr === undefined || $isDecoratorNode(curr)) {
      break;
    }

    if ($isTextNode(curr) || $isTypeaheadNode(curr)) {
      if (trimTextNodeContent(curr, "end")) {
        break;
      }
      stack.pop();
      continue;
    }

    const child = $isElementNode(curr) ? curr.getLastChild() : null;
    if (child === null) {
      const parent = stack[stack.length - 1];
      if (curr !== root && hasTableAncestor(curr)) {
        break;
      }
      if (curr !== root && !$isToggleTitleNode(parent) && !$isToggleDetailsNode(parent)) {
        curr.remove(true);
      }
      stack.pop();
      continue;
    }

    stack.push(child);
  }

  // Children can't be empty, so add a paragraph if there are no children
  if (root.getChildrenSize() === 0) {
    root.append($createParagraphNode());
  }
};

export const trimSerializedLexicalState = (state: SerializedEditorState): SerializedEditorState => {
  const editor = getHeadlessEditorWithNodes();
  loadHeadlessEditorState(editor, state);
  editor.update(
    () => {
      trimLexicalState();
    },
    { discrete: true }
  );
  return editor.getEditorState().toJSON();
};

export const convertMarkdownToSerializedNodes = (markdown: string): SerializedLexicalNode[] => {
  const editor = getHeadlessEditorWithNodes();
  editor.update(() => $convertFromMarkdownString(markdown, TRANSFORMERS), { discrete: true });
  return editor.getEditorState().toJSON().root.children;
};

const convertToTaskRecursive = (
  node: SerializedLexicalNode
): { titleBuilder: [string, boolean, boolean][]; partialTask: Partial<Task> } => {
  const res: { titleBuilder: [string, boolean, boolean][]; partialTask: Partial<Task> } = {
    titleBuilder: [],
    partialTask: {},
  };

  if (node.type === "text") {
    const { text } = node as SerializedTextNode;
    return {
      ...res,
      titleBuilder: [[text.trim(), text.startsWith(" "), text.endsWith(" ")]],
    };
  }

  if (node.type === "typeahead") {
    const { text } = node as SerializedTypeaheadNode;
    return {
      ...res,
      titleBuilder: [[text.trim(), text.startsWith(" "), text.endsWith(" ")]],
    };
  }

  if (node.type === "entity" || node.type === "smart-match") {
    const { propertyDuid, value } = node as SerializedEntityNode | SerializedSmartMatchNode;
    const property = getProperty(propertyDuid);
    if (!property) {
      return res;
    }

    return {
      ...res,
      partialTask: getPropertyPartialTask(property, res.partialTask, value),
    };
  }

  if (!("children" in node)) {
    return res;
  }

  return (node.children as SerializedLexicalNode[]).map(convertToTaskRecursive).reduce(
    (acc, e) => ({
      titleBuilder: [...acc.titleBuilder, ...e.titleBuilder],
      partialTask: merge(acc.partialTask, e.partialTask),
    }),
    res
  );
};

export const convertToTask = (state: SerializedEditorState): Partial<Task> => {
  const { root } = state;

  const { titleBuilder, partialTask } = convertToTaskRecursive(root);
  let prevEndedWithSpace = false;
  const title = titleBuilder
    .filter(Boolean)
    .reduce((acc, [text, startsWithSpace, endsWithSpace]) => {
      const needsSpace = startsWithSpace || prevEndedWithSpace;
      prevEndedWithSpace = endsWithSpace;
      return needsSpace ? `${acc} ${text}` : `${acc}${text}`;
    }, "")
    .trim();

  return { ...partialTask, title };
};

const getContentRecommendationTypeaheadOptions = (
  namespace: string,
  isEditorEmpty?: boolean,
  isSelectionEmpty?: boolean
): TypeaheadOption[] => {
  const isDoc = namespace.startsWith("doc");
  const editorMode = isDoc
    ? EditorMode.DOC
    : EditorMode[namespace.split("-")[1].toUpperCase() as keyof typeof EditorMode];

  const entries: { value: RecommendationKind; label: string; hide: boolean; requiresContent: boolean }[] = [
    {
      value: RecommendationKind.PROPERTIES,
      label: "Fill out properties",
      hide: editorMode !== EditorMode.TCM,
      requiresContent: false,
    },
    {
      value: RecommendationKind.SUBTASKS,
      label: "Break into subtasks",
      hide: editorMode === EditorMode.DOC,
      requiresContent: false,
    },
    {
      value: RecommendationKind.BRAINSTORM,
      label: "Brainstorm methods",
      hide: editorMode === EditorMode.DOC,
      requiresContent: false,
    },
    {
      value: RecommendationKind.WRITE,
      label: isEditorEmpty ? "Write text" : "Continue writing",
      hide: false,
      requiresContent: false,
    },
    {
      value: RecommendationKind.ACTION_ITEMS,
      label: "Extract action items",
      hide: false,
      requiresContent: true,
    },
    {
      value: RecommendationKind.IMPROVE,
      label: "Improve text",
      hide: false,
      requiresContent: true,
    },
    {
      value: RecommendationKind.SIMPLIFY,
      label: "Simplify text",
      hide: false,
      requiresContent: true,
    },
  ];

  return entries
    .filter((e) => !e.hide)
    .map((e) => ({
      value: e.value as string,
      label: e.label,
      icon: RECOMMENDATION_KIND_TO_ICON_MAP.get(e.value),
      iconArgs: {
        class: RECOMMENDATION_ICON_CLASS,
      },
      ...((isEditorEmpty || isSelectionEmpty) &&
        e.requiresContent && {
          disabled: true,
          disabledReason: getEmptyContentDisabledReason(isEditorEmpty, isSelectionEmpty),
        }),
    }));
};

const getActionsTypeaheadOptions = (
  namespace: string,
  isTypeahead: boolean,
  isEditorEmpty?: boolean,
  isSelectionEmpty?: boolean
): TypeaheadOption[] => {
  const isDoc = namespace.startsWith("doc");

  return [
    {
      value: RecommendationKind.SPLIT,
      label: "Split into new task",
      hide: isDoc || isTypeahead,
    },
    {
      value: RecommendationKind.CONVERT_TO_TASKS,
      label: `Convert to ${isDoc ? "" : "sub"}tasks`,
      hide: isTypeahead,
    },
  ]
    .filter((e) => !e.hide)
    .map((e) => ({
      value: e.value as string,
      label: e.label,
      icon: RECOMMENDATION_KIND_TO_ICON_MAP.get(e.value),
      iconArgs: {
        class: "icon-sm",
      },
      ...(isEditorEmpty || isSelectionEmpty
        ? {
            disabled: true,
            disabledReason: getEmptyContentDisabledReason(isEditorEmpty, isSelectionEmpty),
          }
        : {}),
    }));
};

export const getRecommendationTypeaheadSections = (
  namespace: string,
  isTypeahead: boolean,
  isEditorEmpty?: boolean,
  isTitleEmpty?: boolean,
  isSelectionEmpty?: boolean
): { key: string; options: TypeaheadOption[]; total: number }[] => {
  const pageStore = usePageStore();

  if (!pageStore.isOnline) {
    return [
      { key: RECOMMENDATION_SECTION_KEY_AI, options: [], total: 0 },
      { key: "content", options: [], total: 0 },
      { key: "actions", options: [], total: 0 },
      { key: "translation", options: [], total: 0 },
    ];
  }

  const sections: { key: string; options: TypeaheadOption[]; total: number }[] = [];

  const tellAiOption: TypeaheadOption = {
    value: RecommendationKind.CUSTOM,
    label: "Use AI",
    icon: RecommendationIcon,
    iconArgs: {
      class: RECOMMENDATION_ICON_CLASS,
    },
  };
  sections.push({ key: RECOMMENDATION_SECTION_KEY_AI, options: [tellAiOption], total: 1 });

  const contentOptions = getContentRecommendationTypeaheadOptions(namespace, isEditorEmpty, isSelectionEmpty);
  if (contentOptions.length > 0) {
    sections.push({ key: "content", options: contentOptions, total: contentOptions.length });
  }

  const actionsOptions = getActionsTypeaheadOptions(namespace, isTypeahead, isEditorEmpty, isSelectionEmpty);
  if (actionsOptions.length > 0) {
    sections.push({ key: "actions", options: actionsOptions, total: actionsOptions.length });
  }

  const translationOptions = SORTED_LANGUAGES.map((key) => ({
    value: key,
    label: `Translate to ${key}`,
    icon: RecommendTranslateIcon,
    iconArgs: {
      class: RECOMMENDATION_ICON_CLASS,
    },
    ...(isEditorEmpty || isSelectionEmpty
      ? {
          disabled: true,
          disabledReason: getEmptyContentDisabledReason(isEditorEmpty, isSelectionEmpty),
        }
      : {}),
  }));
  sections.push({ key: "translation", options: translationOptions, total: translationOptions.length });

  if (isTitleEmpty) {
    return sections.map((section) => ({
      ...section,
      options: section.options.map((option) => ({
        ...option,
        disabled: true,
        disabledReason: EMPTY_TITLE_DISABLED_REASON,
      })),
    }));
  }

  return sections;
};

export const insertNodeAtSelection = (node: LexicalNode, selection: RangeSelection) => {
  const anchorNode = selection.anchor.getNode();
  const style = $isTextNode(anchorNode) ? anchorNode.getStyle() : null;
  selection?.insertNodes([node]);
  if (style) {
    const spaceNode = $createTextNode(" ");
    spaceNode.setStyle(style);
    selection?.insertNodes([spaceNode]);
  } else {
    selection?.insertRawText(" ");
  }
};

export const $getSelectionAsMarkdown = (editor: LexicalEditor) => {
  const selection = $getSelection();
  if (!$isRangeSelection(selection) || selection.isCollapsed()) {
    return null;
  }

  const serialized = $generateJSONFromSelectedNodes(editor, selection);

  const headlessEditor = getHeadlessEditorWithNodes();
  let result = "";

  headlessEditor.update(
    () => {
      const root = $getRoot();
      const paragraph = $createParagraphNode();

      $generateNodesFromSerializedNodes(serialized.nodes).forEach((node) => {
        ($isElementNode(node) || $isDecoratorNode(node) ? root : paragraph).append(node);
      });

      if (paragraph.getChildrenSize()) {
        root.append(paragraph);
      }
      result = $convertToMarkdownString(TRANSFORMERS, root);
    },
    { discrete: true }
  );

  return result.trim() || null;
};

export const streamRecommendation = async (args: {
  duid: string;
  kind: RecommendationKind;
  selectedText: string | null;
  language: Language | undefined;
  onUpdate: (text: string, duid: string, end: boolean) => void;
  onError: () => void;
  customPrompt?: string | null;
  previouslySuggestedContent?: string | null;
}) => {
  const { duid, kind, selectedText, language, onUpdate, onError, customPrompt, previouslySuggestedContent } = args;

  const pageStore = usePageStore();

  if (kind === RecommendationKind.CUSTOM && (!customPrompt || !customPrompt.trim())) {
    onError();
    return;
  }
  let recommendationDuid: string | null = null;
  let ended = false;

  const setText = (text: string | undefined, end: boolean) => {
    if (text === undefined || ended) {
      return;
    }
    if (end) {
      ended = true;
    }

    if (!recommendationDuid) {
      recommendationDuid = text.substring(0, 12);
    }

    const normalizedText = text.substring(12);
    if (normalizedText === "") {
      if (end) {
        onUpdate("", recommendationDuid ?? "", true);
      }
      return;
    }

    onUpdate(normalizedText, recommendationDuid ?? "", end);
  };

  let text = "";
  const path = language
    ? `recommendations/stream/translate/${language}/${duid}`
    : `recommendations/stream/content/${kind}/${duid}`;
  const data = language
    ? { newTitle: null, selectedText }
    : {
        newTitle: null,
        selectedText,
        customPrompt: customPrompt ?? null,
        previouslySuggestedContent: previouslySuggestedContent ?? null,
      };

  try {
    await streamPostRequest(
      path,
      data,
      (chunk: string) => {
        text += chunk;
        setText(text, false);
      },
      pageStore
    );
    setText(text, true);
  } catch {
    onError();
  }
};

export const useStreamText = (onUpdate: (text: string) => void) => {
  let currentText = "";
  let targetText = "";
  let currentIndex = 0;
  const isStreaming = ref(false);
  let streamInterval: NodeJS.Timeout | null = null;

  const getStreamingConfig = (): { speedMs: number; charsPerStep: number } => {
    const remaining = targetText.length - currentIndex;
    if (remaining > 500) {
      return { speedMs: 0.025, charsPerStep: 80 };
    }
    if (remaining > 200) {
      return { speedMs: 0.25, charsPerStep: 20 };
    }
    if (remaining > 100) {
      return { speedMs: 0.5, charsPerStep: 5 };
    }
    if (remaining > 50) {
      return { speedMs: 1, charsPerStep: 2 };
    }
    return { speedMs: 2, charsPerStep: 1 };
  };

  const startStreaming = () => {
    if (streamInterval) {
      clearInterval(streamInterval);
    }

    isStreaming.value = true;
    const streamStep = (): void => {
      if (currentIndex >= targetText.length && streamInterval) {
        clearInterval(streamInterval);
        streamInterval = null;
        isStreaming.value = false;
        return;
      }

      const config = getStreamingConfig();
      currentIndex = Math.min(currentIndex + config.charsPerStep, targetText.length);
      currentText = targetText.slice(0, currentIndex);
      onUpdate(currentText);

      if (streamInterval) {
        clearInterval(streamInterval);
        streamInterval = setInterval(streamStep, config.speedMs);
      }
    };

    streamInterval = setInterval(streamStep, getStreamingConfig().speedMs);
  };

  const streamText = (chunk: string) => {
    targetText += chunk;

    if (!streamInterval && currentIndex < targetText.length) {
      startStreaming();
    }
  };

  const stop = () => {
    if (streamInterval) {
      clearInterval(streamInterval);
      streamInterval = null;
      isStreaming.value = false;
    }
    currentText = "";
    targetText = "";
    currentIndex = 0;
  };

  return {
    streamText,
    isStreaming,
    stop,
  };
};
